import React, { useEffect, useState, useRef } from "react";
import { 
    Box, Container, Grid, Card, Typography, List, ListItem, ListItemAvatar, 
    Avatar, TextField, Button, Divider, Badge, Dialog, DialogTitle, 
    DialogContent, DialogActions, FormControlLabel, Table, TableBody, 
    TableCell, TableHead, TableRow, IconButton, Checkbox, Chip, Tooltip, InputAdornment
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SearchIcon from "@mui/icons-material/Search";
import AddCommentRoundedIcon from "@mui/icons-material/AddCommentRounded";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import CloseIcon from "@mui/icons-material/Close";
import PhotoLibraryRoundedIcon from "@mui/icons-material/PhotoLibraryRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PendingActionsRoundedIcon from "@mui/icons-material/PendingActionsRounded";
import SyncProblemRoundedIcon from "@mui/icons-material/SyncProblemRounded";
import { DashboardLayout } from "../../components/dashboard-layout";
import { ThemeProvider } from "@mui/material/styles";
import { theme } from "../../theme";
import axios from "axios";
import io from "socket.io-client";

// Helper chuyển đường dẫn file uploads thành URL tuyệt đối chuẩn
const getMediaUrl = (url) => {
    if (!url) return "";
    let cleanStr = String(url).trim();
    const mainServer = process.env.REACT_APP_API_URL_MAIN || "https://server.mm68bet.com";

    // Nếu URL chứa domain server2, tự động chuyển về domain chứa uploads thật (server.mm68bet.com)
    if (cleanStr.includes("server2.mm68bet.com")) {
        cleanStr = cleanStr.replace(/https?:\/\/server2\.mm68bet\.com/i, mainServer);
    }

    if (cleanStr.startsWith("http://") || cleanStr.startsWith("https://") || cleanStr.startsWith("data:")) {
        return cleanStr;
    }
    
    const cleanBase = mainServer.endsWith("/") ? mainServer.slice(0, -1) : mainServer;
    const cleanPath = cleanStr.startsWith("/") ? cleanStr : `/${cleanStr}`;
    return `${cleanBase}${cleanPath}`;
};

// Kiểm tra có phải tin nhắn ảnh hay không
const isImageUrl = (msgText) => {
    if (!msgText) return false;
    const text = String(msgText).trim().toLowerCase();
    return text.startsWith("/uploads/") || text.startsWith("uploads/") || /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(text);
};

const panelSx = { 
    borderRadius: "12px", 
    border: "1px solid #cbd5e1", 
    boxShadow: "0 1px 3px 0 rgba(0,0,0,0.04)", 
    overflow: "hidden" 
};

function ChatAdmin() {
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [openNewMsg, setOpenNewMsg] = useState(false);
    const [newMsgUser, setNewMsgUser] = useState("");
    const [newMsgText, setNewMsgText] = useState("");
    const socketRef = useRef(null);
    const chatEndRef = useRef(null);
    const [statusFilter, setStatusFilter] = useState("Tất cả");
    const [searchKeyword, setSearchKeyword] = useState("");
    
    // Tin nhắn mẫu
    const [templates, setTemplates] = useState([]);
    const [openTemplatesModal, setOpenTemplatesModal] = useState(false);
    const [templateText, setTemplateText] = useState("");
    const [templateIsAuto, setTemplateIsAuto] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    const [showTemplatePicker, setShowTemplatePicker] = useState(false);

    // Sửa tin nhắn chat lẻ
    const [editingMessageId, setEditingMessageId] = useState(null);
    const [editingMessageText, setEditingMessageText] = useState("");

    // Lightbox xem ảnh kích thước lớn
    const [previewImage, setPreviewImage] = useState(null);

    axios.interceptors.request.use(
        (config) => {
            const token = localStorage.getItem("user");
            if (token) {
                config.headers["Authorization"] = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    const formatTimeFull = (dateStr) => {
        if (!dateStr) return "";
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return "";
        const day = ("0" + d.getDate()).slice(-2);
        const month = ("0" + (d.getMonth() + 1)).slice(-2);
        const year = d.getFullYear();
        const hours = ("0" + d.getHours()).slice(-2);
        const minutes = ("0" + d.getMinutes()).slice(-2);
        const seconds = ("0" + d.getSeconds()).slice(-2);
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    };

    const loadRooms = () => {
        let url = `${process.env.REACT_APP_API_URL}/chat/rooms?status=${statusFilter}`;
        if (searchKeyword.trim() !== "") {
            url += `&keyword=${encodeURIComponent(searchKeyword)}`;
        }
        axios.get(url)
            .then(res => {
                if (res.data.success) {
                    setRooms(res.data.data || []);
                }
            }).catch(err => console.error("Lỗi tải danh sách phòng chat:", err));
    };

    const loadTemplates = () => {
        axios.get(`${process.env.REACT_APP_API_URL}/chat/templates`)
            .then(res => {
                if (res.data.success) {
                    setTemplates(res.data.data || []);
                }
            }).catch(err => console.error("Lỗi tải tin nhắn mẫu:", err));
    };

    const loadHistory = (userId) => {
        axios.get(`${process.env.REACT_APP_API_URL}/chat/history/${userId}`)
            .then(res => {
                if (res.data.success) {
                    setMessages(res.data.data || []);
                }
            }).catch(err => console.error("Lỗi tải lịch sử chat:", err));
    };

    useEffect(() => {
        loadRooms();
    }, [statusFilter, searchKeyword]);

    useEffect(() => {
        loadTemplates();
    }, []);

    useEffect(() => {
        const socketUrl = process.env.REACT_APP_API_URL_SOCKET_TXMD560S;
        if (socketUrl) {
            socketRef.current = io(socketUrl, { transports: ["websocket"] });

            socketRef.current.on("new_message_to_admin", (data) => {
                loadRooms();
                if (selectedRoom && selectedRoom._id === data.userId) {
                    setMessages(prev => [...prev, {
                        _id: data._id,
                        user: data.userId,
                        sender: "USER",
                        message: data.message,
                        createdAt: data.createdAt
                    }]);
                }
            });
        }

        return () => {
            if (socketRef.current) {
                socketRef.current.disconnect();
            }
        };
    }, [selectedRoom, statusFilter]);

    useEffect(() => {
        if (chatEndRef.current) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    const handleSelectRoom = (room) => {
        setSelectedRoom(room);
        loadHistory(room._id);
        setRooms(prev => prev.map(r => r._id === room._id ? { ...r, unreadCount: 0 } : r));
    };

    const handleSendMessage = (e) => {
        if (e) e.preventDefault();
        if (!input.trim() || !selectedRoom) return;

        axios.post(`${process.env.REACT_APP_API_URL}/chat/send`, {
            userId: selectedRoom._id,
            message: input
        }).then(res => {
            if (res.data.success) {
                setMessages(prev => [...prev, res.data.data]);
                setInput("");
                loadRooms();
            }
        }).catch(err => console.error("Lỗi gửi tin nhắn:", err));
    };

    const handleDeleteMessage = (msgId) => {
        import("sweetalert").then((swalModule) => {
            const swal = swalModule.default;
            swal({
                title: "Xác nhận xóa?",
                text: "Tin nhắn này sẽ bị xóa vĩnh viễn.",
                icon: "warning",
                buttons: ["Hủy", "Xóa"],
                dangerMode: true,
            }).then((willDelete) => {
                if (willDelete) {
                    axios.delete(`${process.env.REACT_APP_API_URL}/chat/message/${msgId}`)
                        .then((res) => {
                            if (res.data.success) {
                                setMessages(prev => prev.filter(m => m._id !== msgId));
                            }
                        }).catch(() => swal("Lỗi", "Không thể xóa tin nhắn", "error"));
                }
            });
        });
    };

    const handleEditMessageSubmit = () => {
        if (!editingMessageText.trim()) return;
        axios.put(`${process.env.REACT_APP_API_URL}/chat/message/${editingMessageId}`, {
            message: editingMessageText
        }).then(res => {
            if (res.data.success) {
                setMessages(prev => prev.map(m => m._id === editingMessageId ? { ...m, message: editingMessageText } : m));
                setEditingMessageId(null);
                setEditingMessageText("");
            }
        }).catch(() => alert("Lỗi sửa tin nhắn"));
    };

    const handleSendNewMessage = () => {
        if (!newMsgUser.trim() || !newMsgText.trim()) return;
        
        axios.post(`${process.env.REACT_APP_API_URL}/chat/send`, {
            username: newMsgUser, 
            message: newMsgText
        }).then(res => {
            if (res.data.success) {
                import("sweetalert").then((swalModule) => {
                    swalModule.default("Thành công", "Đã gửi tin nhắn đến hội thoại!", "success");
                });
                setOpenNewMsg(false);
                setNewMsgUser("");
                setNewMsgText("");
                loadRooms();
            } else {
                import("sweetalert").then((swalModule) => {
                    swalModule.default("Lỗi", res.data.message || "Không gửi được tin nhắn", "error");
                });
            }
        }).catch(err => {
            import("sweetalert").then((swalModule) => {
                swalModule.default("Lỗi", err.response?.data?.message || "Lỗi máy chủ", "error");
            });
        });
    };

    const updateChatStatus = (status) => {
        if (!selectedRoom) return;
        axios.post(`${process.env.REACT_APP_API_URL}/chat/update-status`, {
            userId: selectedRoom._id,
            status: status
        }).then(res => {
            if (res.data.success) {
                setSelectedRoom(prev => ({
                    ...prev,
                    user: { ...prev.user, chatStatus: status }
                }));
                loadRooms();
            }
        }).catch(err => console.error("Lỗi cập nhật trạng thái chat:", err));
    };

    const handleSaveTemplate = () => {
        if (!templateText.trim()) return;
        const payload = { content: templateText, isAutoReply: templateIsAuto };
        if (editingTemplateId) {
            axios.put(`${process.env.REACT_APP_API_URL}/chat/templates/${editingTemplateId}`, payload)
                .then(res => {
                    if (res.data.success) {
                        setEditingTemplateId(null);
                        setTemplateText("");
                        setTemplateIsAuto(false);
                        loadTemplates();
                    }
                });
        } else {
            axios.post(`${process.env.REACT_APP_API_URL}/chat/templates`, payload)
                .then(res => {
                    if (res.data.success) {
                        setTemplateText("");
                        setTemplateIsAuto(false);
                        loadTemplates();
                    }
                });
        }
    };

    const handleDeleteTemplate = (id) => {
        axios.delete(`${process.env.REACT_APP_API_URL}/chat/templates/${id}`)
            .then(res => {
                if (res.data.success) {
                    loadTemplates();
                }
            });
    };

    const formatMoney = (n) => Number(n || 0).toLocaleString("vi-VN");
    const formatPoints = (n) => Math.floor(Number(n || 0) / 1000).toLocaleString("vi-VN");

    const getStatusChipProps = (status) => {
        if (status === "Đã xử lý") return { color: "success", label: "Đã xử lý", icon: <CheckCircleRoundedIcon fontSize="small" /> };
        if (status === "Đang xử lý") return { color: "warning", label: "Đang xử lý", icon: <PendingActionsRoundedIcon fontSize="small" /> };
        return { color: "error", label: "Chờ xử lý", icon: <SyncProblemRoundedIcon fontSize="small" /> };
    };

    return (
        <ThemeProvider theme={theme}>
            <DashboardLayout>
                <Box component="main" className="blockchain-admin-page" sx={{ flexGrow: 1, py: 2.5, px: 2, background: "#f8fafc", minHeight: "100vh" }}>
                    <Container maxWidth="xl" disableGutters>
                        
                        {/* Header Toolbar CSKH */}
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 500, color: "#0f172a", fontSize: "20px" }}>
                                    Hộp thư Hỗ trợ CSKH 24/7
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 300, fontSize: "13px" }}>
                                    Quản lý tin nhắn phản hồi trực tiếp với người chơi
                                </Typography>
                            </Box>

                            <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                                <Button 
                                    variant="outlined" 
                                    startIcon={<FormatQuoteRoundedIcon />} 
                                    onClick={() => setOpenTemplatesModal(true)}
                                    sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 400, bgcolor: "#ffffff" }}
                                >
                                    Tin nhắn mẫu ({templates.length})
                                </Button>
                                <Button 
                                    variant="contained" 
                                    startIcon={<AddCommentRoundedIcon />} 
                                    onClick={() => setOpenNewMsg(true)}
                                    sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 400, bgcolor: "#2563eb", "&:hover": { bgcolor: "#1d4ed8" } }}
                                >
                                    Tạo tin nhắn mới
                                </Button>
                            </Box>
                        </Box>

                        {/* Layout Chat Split: Left (Phòng chat) - Right (Khung chat) */}
                        <Grid container spacing={2} sx={{ height: "calc(100vh - 170px)", minHeight: 600 }}>
                            
                            {/* Cột Trái: Danh sách hội thoại */}
                            <Grid item xs={12} md={4} lg={3.5} sx={{ height: "100%" }}>
                                <Card sx={{ ...panelSx, height: "100%", display: "flex", flexDirection: "column", bgcolor: "#ffffff" }}>
                                    
                                    {/* Tìm kiếm & BỘ LỌC TRẠNG THÁI */}
                                    <Box sx={{ p: 1.5, borderBottom: "1px solid #e2e8f0", bgcolor: "#f8fafc" }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Tìm username / ID..."
                                            value={searchKeyword}
                                            onChange={(e) => setSearchKeyword(e.target.value)}
                                            InputProps={{
                                                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: "#94a3b8", fontSize: 20 }} /></InputAdornment>
                                            }}
                                            sx={{ mb: 1.2, bgcolor: "#ffffff", borderRadius: "8px" }}
                                        />
                                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                            {["Tất cả", "Chờ xử lý", "Đang xử lý", "Đã xử lý"].map((st) => (
                                                <Chip
                                                    key={st}
                                                    size="small"
                                                    label={st}
                                                    clickable
                                                    variant={statusFilter === st ? "filled" : "outlined"}
                                                    color={statusFilter === st ? (st === "Đã xử lý" ? "success" : st === "Đang xử lý" ? "warning" : st === "Chờ xử lý" ? "error" : "primary") : "default"}
                                                    onClick={() => setStatusFilter(st)}
                                                    sx={{ fontWeight: 400, borderRadius: "6px", fontSize: "12px" }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>

                                    {/* Danh sách các cuộc trò chuyện */}
                                    <List sx={{ flexGrow: 1, overflowY: "auto", py: 0 }}>
                                        {rooms.length > 0 ? (
                                            rooms.map((room) => {
                                                const isSelected = selectedRoom?._id === room._id;
                                                const isImgMsg = isImageUrl(room.lastMessage);
                                                const chipProps = getStatusChipProps(room.user?.chatStatus || "Chờ xử lý");
                                                return (
                                                    <ListItem
                                                        key={room._id}
                                                        button
                                                        selected={isSelected}
                                                        onClick={() => handleSelectRoom(room)}
                                                        sx={{
                                                            py: 1.5,
                                                            px: 2,
                                                            borderBottom: "1px solid #f1f5f9",
                                                            bgcolor: isSelected ? "#eff6ff" : "transparent",
                                                            borderLeft: isSelected ? "4px solid #2563eb" : "4px solid transparent",
                                                            "&:hover": { bgcolor: isSelected ? "#eff6ff" : "#f8fafc" }
                                                        }}
                                                    >
                                                        <ListItemAvatar>
                                                            <Badge badgeContent={room.unreadCount} color="error" max={99}>
                                                                <Avatar sx={{ bgcolor: "#3b82f6", fontWeight: 500, width: 40, height: 40, fontSize: 15 }}>
                                                                    {room.user?.username ? room.user.username[0].toUpperCase() : "U"}
                                                                </Avatar>
                                                            </Badge>
                                                        </ListItemAvatar>
                                                        <Box sx={{ minWidth: 0, flexGrow: 1, ml: 0.5 }}>
                                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.3 }}>
                                                                <Typography sx={{ fontWeight: 500, color: "#0f172a", fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                                    {room.user?.username || "Người dùng"}
                                                                </Typography>
                                                                <Chip size="small" variant="outlined" color={chipProps.color} label={chipProps.label} sx={{ height: 20, fontSize: "10px", fontWeight: 400, borderRadius: "4px" }} />
                                                            </Box>
                                                            <Typography sx={{ color: isImgMsg ? "#2563eb" : "#64748b", fontSize: "12px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: room.unreadCount > 0 ? 500 : 300 }}>
                                                                {isImgMsg ? "📷 [Hình ảnh đính kèm]" : (room.lastMessage || "Chưa có tin nhắn")}
                                                            </Typography>
                                                        </Box>
                                                    </ListItem>
                                                );
                                            })
                                        ) : (
                                            <Box sx={{ p: 4, textAlign: "center", color: "#94a3b8" }}>
                                                <Typography variant="body2" sx={{ fontWeight: 300 }}>Không tìm thấy hội thoại nào.</Typography>
                                            </Box>
                                        )}
                                    </List>
                                </Card>
                            </Grid>

                            {/* Cột Phải: Khung Chat Chi Tiết */}
                            <Grid item xs={12} md={8} lg={8.5} sx={{ height: "100%" }}>
                                <Card sx={{ ...panelSx, height: "100%", display: "flex", flexDirection: "column", bgcolor: "#ffffff" }}>
                                    {selectedRoom ? (
                                        <>
                                            {/* Header Khung Chat */}
                                            <Box sx={{ p: 2, bgcolor: "#ffffff", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5 }}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                        <Typography variant="h6" sx={{ fontWeight: 500, color: "#0f172a", fontSize: "16px" }}>
                                                            {selectedRoom.user?.username}
                                                        </Typography>
                                                        <Chip size="small" label={`ID: ${selectedRoom.user?.iduser || selectedRoom.user?._id || "—"}`} sx={{ bgcolor: "#f1f5f9", fontWeight: 400, fontSize: "11px" }} />
                                                    </Box>
                                                    
                                                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mt: 0.5, flexWrap: "wrap" }}>
                                                        <Typography sx={{ fontSize: "12px", color: "#16a34a", fontWeight: 400 }}>Nạp: {formatPoints(selectedRoom.user?.tongnap)} điểm</Typography>
                                                        <Typography sx={{ fontSize: "12px", color: "#dc2626", fontWeight: 400 }}>Rút: {formatPoints(selectedRoom.user?.tongrut)} điểm</Typography>
                                                        <Typography sx={{ fontSize: "12px", color: "#d97706", fontWeight: 400 }}>Số dư: {formatPoints(selectedRoom.user?.money)} điểm</Typography>
                                                        <Typography sx={{ fontSize: "12px", color: "#64748b", fontWeight: 300 }}>IP: {selectedRoom.user?.ip || "0.0.0.0"}</Typography>
                                                    </Box>
                                                </Box>

                                                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                                                    {selectedRoom.user?.chatStatus === "Chờ xử lý" && (
                                                        <Button size="small" variant="contained" color="warning" onClick={() => updateChatStatus("Đang xử lý")} sx={{ textTransform: "none", fontWeight: 400, borderRadius: "6px" }}>
                                                            Đang xử lý
                                                        </Button>
                                                    )}
                                                    {selectedRoom.user?.chatStatus !== "Đã xử lý" && (
                                                        <Button size="small" variant="contained" color="success" onClick={() => updateChatStatus("Đã xử lý")} sx={{ textTransform: "none", fontWeight: 400, borderRadius: "6px" }}>
                                                            Đã xử lý
                                                        </Button>
                                                    )}
                                                    <Button 
                                                        size="small" 
                                                        variant="outlined" 
                                                        color="error" 
                                                        onClick={() => {
                                                            import("sweetalert").then((swalModule) => {
                                                                const swal = swalModule.default;
                                                                swal({
                                                                    title: "Xác nhận xóa?",
                                                                    text: "Bạn có chắc chắn muốn xóa toàn bộ lịch sử cuộc trò chuyện này?",
                                                                    icon: "warning",
                                                                    buttons: ["Hủy", "Xóa toàn bộ"],
                                                                    dangerMode: true,
                                                                }).then((willDelete) => {
                                                                    if (willDelete) {
                                                                        axios.delete(`${process.env.REACT_APP_API_URL}/chat/history/${selectedRoom._id}`)
                                                                            .then(() => {
                                                                                swal("Thành công", "Đã xóa lịch sử chat!", "success");
                                                                                setMessages([]);
                                                                                setSelectedRoom(null);
                                                                                loadRooms();
                                                                            }).catch(() => swal("Thất bại", "Lỗi khi xóa cuộc trò chuyện", "error"));
                                                                    }
                                                                });
                                                            });
                                                        }}
                                                        sx={{ textTransform: "none", fontWeight: 400, borderRadius: "6px" }}
                                                    >
                                                        Xóa đoạn chat
                                                    </Button>
                                                </Box>
                                            </Box>

                                            {/* Danh sách Tin Nhắn Chat */}
                                            <Box sx={{ flexGrow: 1, p: 2.5, overflowY: "auto", bgcolor: "#f8fafc", display: "flex", flexDirection: "column", gap: 1.5 }}>
                                                {messages.map((msg, index) => {
                                                    const isMe = msg.sender === "ADMIN";
                                                    const isImage = isImageUrl(msg.message);
                                                    const fullImgUrl = getMediaUrl(msg.message);

                                                    return (
                                                        <Box
                                                            key={index}
                                                            sx={{
                                                                display: "flex",
                                                                justifyContent: isMe ? "flex-end" : "flex-start",
                                                                mb: 0.5
                                                            }}
                                                        >
                                                            <Box sx={{ maxWidth: "72%", position: "relative", "&:hover .msg-actions": { opacity: 1 } }}>
                                                                
                                                                {/* Hover Action toolbar */}
                                                                <Box className="msg-actions" sx={{
                                                                    position: "absolute",
                                                                    top: -14,
                                                                    right: isMe ? "auto" : -28,
                                                                    left: isMe ? -28 : "auto",
                                                                    display: "flex",
                                                                    opacity: 0,
                                                                    transition: "opacity 0.2s ease",
                                                                    bgcolor: "#ffffff",
                                                                    borderRadius: "6px",
                                                                    border: "1px solid #cbd5e1",
                                                                    boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
                                                                    zIndex: 2
                                                                }}>
                                                                    {!isImage && (
                                                                        <IconButton size="small" color="primary" onClick={() => {
                                                                            setEditingMessageId(msg._id);
                                                                            setEditingMessageText(msg.message);
                                                                        }}>
                                                                            <EditIcon fontSize="small" sx={{ fontSize: 16 }} />
                                                                        </IconButton>
                                                                    )}
                                                                    <IconButton size="small" color="error" onClick={() => handleDeleteMessage(msg._id)}>
                                                                        <DeleteIcon fontSize="small" sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                </Box>

                                                                <Box sx={{
                                                                    p: 1.5,
                                                                    bgcolor: isMe ? "#2563eb !important" : "#ffffff !important",
                                                                    background: isMe ? "#2563eb !important" : "#ffffff !important",
                                                                    color: isMe ? "#ffffff !important" : "#0f172a !important",
                                                                    borderRadius: isMe ? "14px 14px 2px 14px" : "14px 14px 14px 2px",
                                                                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                                                                    border: isMe ? "none" : "1px solid #e2e8f0"
                                                                }}>
                                                                    {editingMessageId === msg._id ? (
                                                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, minWidth: 220 }}>
                                                                            <TextField 
                                                                                size="small" 
                                                                                multiline 
                                                                                value={editingMessageText} 
                                                                                onChange={e => setEditingMessageText(e.target.value)} 
                                                                                sx={{ bgcolor: "#ffffff", borderRadius: "6px" }}
                                                                            />
                                                                            <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                                                                                <Button size="small" variant="contained" color="success" onClick={handleEditMessageSubmit} sx={{ textTransform: "none" }}>Lưu</Button>
                                                                                <Button size="small" variant="outlined" color="inherit" onClick={() => setEditingMessageId(null)} sx={{ textTransform: "none" }}>Hủy</Button>
                                                                            </Box>
                                                                        </Box>
                                                                    ) : (
                                                                        <>
                                                                            {isImage ? (
                                                                                <Box sx={{ position: "relative" }}>
                                                                                    <img 
                                                                                        src={fullImgUrl} 
                                                                                        alt="Đính kèm" 
                                                                                        style={{ maxWidth: "100%", maxHeight: "240px", borderRadius: "8px", cursor: "pointer", display: "block", objectFit: "contain" }} 
                                                                                        onClick={() => setPreviewImage(fullImgUrl)}
                                                                                        onError={(e) => {
                                                                                            const mainServer = process.env.REACT_APP_API_URL_MAIN || "https://server.mm68bet.com";
                                                                                            const currentSrc = e.target.src;
                                                                                            if (currentSrc.includes("server2.mm68bet.com")) {
                                                                                                e.target.src = currentSrc.replace(/https?:\/\/server2\.mm68bet\.com/i, mainServer);
                                                                                            } else {
                                                                                                e.target.onerror = null;
                                                                                                e.target.style.display = 'none';
                                                                                                if (e.target.parentNode) {
                                                                                                    e.target.parentNode.innerHTML = `<div style="padding:10px;color:#ef4444;font-size:12px;background:#fef2f2;border-radius:6px;border:1px solid #fecaca">⚠️ Không tải được ảnh đính kèm</div>`;
                                                                                                }
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                    <Button 
                                                                                        size="small" 
                                                                                        startIcon={<OpenInNewRoundedIcon fontSize="small" />}
                                                                                        onClick={() => window.open(fullImgUrl, "_blank")}
                                                                                        sx={{ mt: 0.8, color: isMe ? "#ffffff !important" : "#2563eb !important", textTransform: "none", fontSize: "11px", fontWeight: 400, p: 0 }}
                                                                                    >
                                                                                        Mở ảnh tab mới
                                                                                    </Button>
                                                                                </Box>
                                                                            ) : (
                                                                                <Typography variant="body2" sx={{ color: isMe ? "#ffffff !important" : "#0f172a !important", wordBreak: "break-word", whiteSpace: "pre-line", fontWeight: 400, fontSize: "14px", lineHeight: 1.5 }}>
                                                                                    {msg.message}
                                                                                </Typography>
                                                                            )}
                                                                            <Typography variant="caption" display="block" sx={{ textAlign: "right", color: isMe ? "rgba(255,255,255,0.85) !important" : "#64748b !important", opacity: 0.85, mt: 0.5, fontSize: "10px", fontWeight: 300 }}>
                                                                                {formatTimeFull(msg.createdAt)}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                                <div ref={chatEndRef} />
                                            </Box>

                                            {/* Quick Replies Picker Drawer */}
                                            {showTemplatePicker && (
                                                <Box sx={{ bgcolor: "#ffffff", borderTop: "1px solid #e2e8f0", p: 1.5, display: "flex", flexWrap: "wrap", gap: 1, maxHeight: 150, overflowY: "auto" }}>
                                                    {templates.map(t => (
                                                        <Button 
                                                            key={t._id} 
                                                            variant="outlined" 
                                                            size="small" 
                                                            onClick={() => {
                                                                setInput(t.content);
                                                                setShowTemplatePicker(false);
                                                            }}
                                                            sx={{ borderRadius: "16px", textTransform: "none", py: "3px", px: "12px", fontWeight: 400, borderColor: "#cbd5e1", color: "#334155" }}
                                                        >
                                                            {t.content.length > 45 ? t.content.substring(0, 45) + "..." : t.content}
                                                        </Button>
                                                    ))}
                                                    {templates.length === 0 && <Typography variant="caption" sx={{ p: 1, color: "#64748b" }}>Chưa tạo tin nhắn mẫu nào.</Typography>}
                                                </Box>
                                            )}

                                            {/* Bar Nhập Tin Nhắn */}
                                            <Box component="form" onSubmit={handleSendMessage} sx={{ p: 1.5, bgcolor: "#ffffff", borderTop: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 1 }}>
                                                <Button 
                                                    variant="outlined" 
                                                    size="small" 
                                                    onClick={() => setShowTemplatePicker(!showTemplatePicker)}
                                                    sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 400, minWidth: 64, height: 38 }}
                                                >
                                                    Mẫu
                                                </Button>

                                                <input 
                                                    type="file" 
                                                    id="admin-chat-file-input"
                                                    onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (!file || !selectedRoom) return;

                                                        const formData = new FormData();
                                                        formData.append("file", file);

                                                        axios.post(`${process.env.REACT_APP_API_URL}/chat/upload`, formData, {
                                                            headers: { "Content-Type": "multipart/form-data" }
                                                        }).then(res => {
                                                            if (res.data.success) {
                                                                axios.post(`${process.env.REACT_APP_API_URL}/chat/send`, {
                                                                    userId: selectedRoom._id,
                                                                    message: res.data.fileUrl
                                                                }).then(sendRes => {
                                                                    if (sendRes.data.success) {
                                                                        setMessages(prev => [...prev, sendRes.data.data]);
                                                                        loadRooms();
                                                                    }
                                                                });
                                                            }
                                                        }).catch(err => {
                                                            console.error("Lỗi gửi file đính kèm:", err);
                                                        });
                                                    }}
                                                    style={{ display: "none" }} 
                                                    accept="image/*"
                                                />
                                                <IconButton 
                                                    onClick={() => document.getElementById("admin-chat-file-input").click()} 
                                                    color="primary"
                                                    title="Gửi ảnh đính kèm"
                                                    sx={{ bgcolor: "#eff6ff", "&:hover": { bgcolor: "#dbeafe" } }}
                                                >
                                                    <AttachFileIcon />
                                                </IconButton>

                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    placeholder="Nhập nội dung phản hồi..."
                                                    value={input}
                                                    onChange={(e) => setInput(e.target.value)}
                                                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                                                />

                                                <Button 
                                                    type="submit" 
                                                    variant="contained" 
                                                    disabled={!input.trim()}
                                                    endIcon={<SendIcon />}
                                                    sx={{ height: 38, borderRadius: "8px", textTransform: "none", fontWeight: 400, px: 2.5 }}
                                                >
                                                    Gửi
                                                </Button>
                                            </Box>
                                        </>
                                    ) : (
                                        <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: "100%", color: "#94a3b8", gap: 1 }}>
                                            <PhotoLibraryRoundedIcon sx={{ fontSize: 48, color: "#cbd5e1" }} />
                                            <Typography variant="body1" sx={{ fontWeight: 300 }}>Chọn một hội thoại bên trái để bắt đầu hỗ trợ người chơi.</Typography>
                                        </Box>
                                    )}
                                </Card>
                            </Grid>
                        </Grid>
                    </Container>

                    {/* Dialog xem ảnh kích thước lớn (Lightbox Preview) */}
                    <Dialog open={Boolean(previewImage)} onClose={() => setPreviewImage(null)} maxWidth="md" fullWidth>
                        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 1.5 }}>
                            <Typography variant="body1" fontWeight={500}>Xem ảnh đính kèm</Typography>
                            <IconButton onClick={() => setPreviewImage(null)}><CloseIcon /></IconButton>
                        </DialogTitle>
                        <DialogContent dividers sx={{ textAlign: "center", bgcolor: "#0f172a", p: 2 }}>
                            {previewImage && (
                                <img src={previewImage} alt="Kích thước lớn" style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain", borderRadius: "8px" }} />
                            )}
                        </DialogContent>
                        <DialogActions sx={{ p: 1.5 }}>
                            <Button startIcon={<OpenInNewRoundedIcon />} onClick={() => window.open(previewImage, "_blank")} sx={{ textTransform: "none" }}>Mở tab mới</Button>
                            <Button variant="outlined" onClick={() => setPreviewImage(null)} sx={{ textTransform: "none" }}>Đóng</Button>
                        </DialogActions>
                    </Dialog>

                    {/* Dialog Tạo tin nhắn mới */}
                    <Dialog open={openNewMsg} onClose={() => setOpenNewMsg(false)} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: "12px" } }}>
                        <DialogTitle sx={{ fontWeight: 500 }}>Tạo tin nhắn mới tới người chơi</DialogTitle>
                        <DialogContent dividers>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Tên đăng nhập (Username)"
                                type="text"
                                fullWidth
                                size="small"
                                value={newMsgUser}
                                onChange={(e) => setNewMsgUser(e.target.value)}
                            />
                            <TextField
                                margin="dense"
                                label="Nội dung tin nhắn"
                                type="text"
                                fullWidth
                                multiline
                                rows={4}
                                size="small"
                                value={newMsgText}
                                onChange={(e) => setNewMsgText(e.target.value)}
                                sx={{ mt: 2 }}
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 3, py: 2 }}>
                            <Button onClick={() => setOpenNewMsg(false)} sx={{ textTransform: "none" }}>Hủy</Button>
                            <Button onClick={handleSendNewMessage} variant="contained" color="primary" sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 400, px: 3 }}>Gửi</Button>
                        </DialogActions>
                    </Dialog>

                    {/* Dialog Quản lý tin nhắn mẫu */}
                    <Dialog open={openTemplatesModal} onClose={() => setOpenTemplatesModal(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
                        <DialogTitle sx={{ fontWeight: 500 }}>Quản lý Tin Nhắn Mẫu & Phản Hồi Tự Động</DialogTitle>
                        <DialogContent dividers>
                            <Box sx={{ display: "flex", gap: 2, mb: 3, alignItems: "center", flexWrap: "wrap" }}>
                                <TextField 
                                    label="Nội dung tin nhắn mẫu" 
                                    size="small"
                                    fullWidth 
                                    value={templateText} 
                                    onChange={(e) => setTemplateText(e.target.value)}
                                    multiline
                                    rows={2}
                                />
                                <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "100%", justifyContent: "space-between" }}>
                                    <FormControlLabel
                                        control={<Checkbox checked={templateIsAuto} onChange={(e) => setTemplateIsAuto(e.target.checked)} color="primary" />}
                                        label={<Typography fontSize={13} fontWeight={400}>Tự động trả lời khi người chơi nhắn tin lần đầu</Typography>}
                                    />
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <Button variant="contained" onClick={handleSaveTemplate} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 400 }}>
                                            {editingTemplateId ? "Lưu thay đổi" : "Thêm mẫu mới"}
                                        </Button>
                                        {editingTemplateId && (
                                            <Button variant="outlined" color="inherit" onClick={() => { setEditingTemplateId(null); setTemplateText(""); setTemplateIsAuto(false); }} sx={{ borderRadius: "8px", textTransform: "none" }}>
                                                Hủy
                                            </Button>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ bg: "#f8fafc" }}>
                                        <TableCell sx={{ fontWeight: 500 }}>Nội dung mẫu</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 500 }}>Tự động</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 500 }}>Thao tác</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {templates.map((tpl) => (
                                        <TableRow key={tpl._id} hover>
                                            <TableCell sx={{ maxWidth: 300, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", fontWeight: 400 }} title={tpl.content}>
                                                {tpl.content}
                                            </TableCell>
                                            <TableCell align="center">
                                                {tpl.isAutoReply ? <Chip size="small" label="Tự động" color="success" sx={{ height: 20, fontSize: 10, fontWeight: 400 }} /> : "—"}
                                            </TableCell>
                                            <TableCell align="right">
                                                <IconButton color="primary" onClick={() => {
                                                    setEditingTemplateId(tpl._id);
                                                    setTemplateText(tpl.content);
                                                    setTemplateIsAuto(tpl.isAutoReply);
                                                }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton color="error" onClick={() => handleDeleteTemplate(tpl._id)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {templates.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ py: 4, color: "#94a3b8", fontWeight: 300 }}>Chưa có tin nhắn mẫu nào được tạo.</TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, py: 2 }}>
                            <Button variant="outlined" onClick={() => setOpenTemplatesModal(false)} sx={{ borderRadius: "8px", textTransform: "none" }}>Đóng</Button>
                        </DialogActions>
                    </Dialog>

                </Box>
            </DashboardLayout>
        </ThemeProvider>
    );
}

export default ChatAdmin;
